import React, { useState, useRef } from 'react';
import { IconButton } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { cancelarVoz } from '../../util/voice';
import { StopCircle } from '@mui/icons-material';
import { UAParser } from 'ua-parser-js';
import { liaResponseByAudio } from '../../apis/ia';

interface AudioParaTextoProps {
  onTranscricao?: (texto: string) => void;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  conversationId?: string | null | undefined;
  scrollToBottom?: (force?: boolean) => void;
  onResolveConversationId?: () => Promise<string | undefined>;
}

const AudioParaTexto: React.FC<AudioParaTextoProps> = ({
  onTranscricao,
  loading,
  setLoading,
  conversationId,
  scrollToBottom,
  onResolveConversationId,
}) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoldingRef = useRef(false);
  const finalizedRef = useRef(false);
  const recorderRef = useRef<{
    stopRecording: () => void;
    audioBlobPromise: Promise<Blob>;
  } | null>(null);
  const ua = new UAParser().getResult();
  const isChrome = ua.browser.name === 'Chrome';

  function createSilenceDetector(
    stream: MediaStream,
    {
      rmsThreshold = 0.01,
      silenceMillis = 1200,
      maxRecordMillis = 120000,
      armAfterFirstVoice = true,
      maxNoVoiceMillis = null as number | null,
    }: {
      rmsThreshold?: number;
      silenceMillis?: number;
      maxRecordMillis?: number;
      armAfterFirstVoice?: boolean;
      maxNoVoiceMillis?: number | null;
    } = {}
  ) {
    const audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    const buf = new Float32Array(analyser.fftSize);

    let hasHeardVoice = false;
    let lastVoiceTs = 0;
    const startTs = performance.now();
    let stopped = false;
    let stopCallback: (() => void) | null = null;

    const loop = () => {
      if (stopped) return;

      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);

      const now = performance.now();

      if (rms >= rmsThreshold) {
        hasHeardVoice = true;
        lastVoiceTs = now;
      }

      const runningFor = now - startTs;

      if (runningFor >= maxRecordMillis) {
        stopped = true;
        stopCallback && stopCallback();
        return;
      }

      if (
        !hasHeardVoice &&
        maxNoVoiceMillis &&
        runningFor >= maxNoVoiceMillis
      ) {
        stopped = true;
        stopCallback && stopCallback();
        return;
      }

      if (!armAfterFirstVoice || hasHeardVoice) {
        const silentFor = now - (hasHeardVoice ? lastVoiceTs : now);
        if (silentFor >= silenceMillis) {
          stopped = true;
          stopCallback && stopCallback();
          return;
        }
      }

      requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop);

    return {
      onStop(cb: () => void) {
        stopCallback = cb;
      },
      cleanup() {
        try {
          source.disconnect();
        } catch {}
        try {
          analyser.disconnect();
        } catch {}
        try {
          audioCtx.close();
        } catch {}
      },
    };
  }

  const startAudioRecording = ({
    rmsThreshold = 0.03,
    silenceMillis = 1200,
    maxRecordMillis = 120000,
    armAfterFirstVoice = true,
  }: {
    rmsThreshold?: number;
    silenceMillis?: number;
    maxRecordMillis?: number;
    armAfterFirstVoice?: boolean;
  } = {}) => {
    let mediaRecorder: MediaRecorder;
    let silence: ReturnType<typeof createSilenceDetector> | null = null;
    let stream: MediaStream | null = null;
    const chunks: BlobPart[] = [];
    let stopping = false;

    const stopNow = () => {
      if (stopping) return;
      stopping = true;
      setListening(false);
      try {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      } catch {}
    };

    const audioBlobPromise = new Promise<Blob>(async (resolve, reject) => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const preferredTypes = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/mp4',
          'audio/aac',
          'audio/mpeg',
        ];
        const mimeType =
          preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || '';

        mediaRecorder = new MediaRecorder(
          stream,
          mimeType ? { mimeType } : undefined
        );

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          try {
            const blobType =
              (mimeType && mimeType.length > 0
                ? mimeType
                : (mediaRecorder as any).mimeType) || 'audio/webm';
            const audioBlob = new Blob(chunks, { type: blobType });
            resolve(audioBlob);
          } finally {
            try {
              stream?.getTracks().forEach((t) => t.stop());
            } catch {}
            silence?.cleanup?.();
          }
        };

        silence = createSilenceDetector(stream, {
          rmsThreshold,
          silenceMillis,
          maxRecordMillis,
          armAfterFirstVoice,
          // Se quiser nunca cortar antes de falar, deixe maxNoVoiceMillis = null
          // Se quiser abortar depois de X segundos sem nunca falar, defina um valor:
          // maxNoVoiceMillis: 15000, // por ex. 15s sem voz → para
        });

        silence.onStop(() => stopNow());

        mediaRecorder.start();
        setListening(true);
      } catch (err) {
        try {
          stream?.getTracks().forEach((t) => t.stop());
        } catch {}
        silence?.cleanup?.();
        reject(err);
      }
    });

    const stopRecording = () => stopNow();

    return { stopRecording, audioBlobPromise };
  };

  const getResponseByAudio = async () => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;

    let finalized = false;

    try {
      if (!recorderRef.current) return;

      scrollToBottom?.(true);

      const blob = await recorderRef.current.audioBlobPromise;
      setLoading?.(true);
      const resolvedConversationId =
        conversationId ?? (await onResolveConversationId?.());
      const result = await liaResponseByAudio(blob, resolvedConversationId);

      onTranscricao?.(result.transcription);
      finalized = true;
    } catch (e) {
      console.error('Falha ao finalizar/transcrever:', e);
    } finally {
      if (!finalized) {
        setLoading?.(false);
      }

      recorderRef.current = null;
      finalizedRef.current = false;
    }
  };

  const startRecognition = () => {
    const SpeechRecognition =
      (window as any)?.SpeechRecognition ||
      (window as any)?.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Este navegador não suporta a Web Speech API.');
      return;
    }
    cancelarVoz();

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const resultado = event.results[0][0].transcript;
      onTranscricao?.(resultado);
    };

    recognition.onerror = (event: any) => {
      console.error('Erro:', event.error);
      if (isHoldingRef.current) {
        setListening(false);
        isHoldingRef.current = false;
      }
    };

    recognition.onend = () => {
      setListening(false);
      isHoldingRef.current = false;
    };

    recognition.start();
  };

  const stopRecognition = () => {
    recognitionRef.current?.stop();
    if (!isHoldingRef.current) {
      setListening(false);
    }
  };

  const handleMouseDown = () => {
    window?.dispatchEvent(new Event('stopAllAudios'));
    cancelarVoz();

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
    }
    isHoldingRef.current = true;

    pressTimerRef.current = setTimeout(() => {
      if (!listening) {
        if (isChrome) {
          startRecognition();
        } else {
          finalizedRef.current = false;
          recorderRef.current = startAudioRecording();
          recorderRef.current.audioBlobPromise
            .then(() => getResponseByAudio())
            .catch((e) => console.error('Erro aguardando blob:', e));
        }
      }
    }, 300);
  };

  const handleMouseUp = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    isHoldingRef.current = false;

    if (!listening) return;

    if (isChrome) {
      stopRecognition();
    } else {
      recorderRef.current?.stopRecording();
    }
  };

  const handleClick = () => {
    if (pressTimerRef.current !== null || isHoldingRef.current) return;

    if (listening) {
      if (isChrome) {
        stopRecognition();
      } else {
        recorderRef.current?.stopRecording();
      }
    } else {
      if (isChrome) {
        startRecognition();
      } else {
        finalizedRef.current = false;
        recorderRef.current = startAudioRecording();
        recorderRef.current.audioBlobPromise
          .then(() => getResponseByAudio())
          .catch((e) => console.error('Erro aguardando blob:', e));
      }
    }
  };

  return (
    <IconButton
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      disabled={loading}
      aria-label={listening ? "Parar gravação de áudio" : "Gravar áudio"}
      sx={{
        color: '#EAF0F4',
        '&.Mui-disabled': {
          color: '#EAF0F4 !important',
          opacity: 1,
        },
        '&.Mui-disabled svg': {
          color: '#EAF0F4 !important',
          fill: '#EAF0F4 !important',
        },
      }}
    >
      {listening ? <StopCircle /> : <MicIcon />}
    </IconButton>
  );
};

export default AudioParaTexto;
